import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { friendlyMessage } from '@/lib/errors';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { authApi } from '@/lib/api/services/auth';
import { useAuth } from '@/components/AuthProvider';
import { validatePhoneForCountry, phoneOverLengthError } from '@/lib/constants/phoneValidation';
import { PhoneNumberInput } from '@/components/PhoneNumberInput';
import { OtpInput } from '@/components/OtpInput';
import { markNewSignupPending } from '@/lib/onboardingCompletion';
import { ONBOARDING_V2_PREFLIGHT_PATH } from '@/components/onboarding-v2/onboardingV2Routes';

export type PhoneAuthStep = 'phone' | 'otp';

export interface PhoneAuthFlowHandle {
  back: () => void;
}

interface PhoneAuthFlowProps {
  onRequiresTotp: (userId: string) => void;
  onSuccess: () => void;
  onStepChange?: (step: PhoneAuthStep, phoneNumberDisplay?: string) => void;
  onSwitchToPassword?: (message: string) => void;
}

function AuthLegalConsent({ className }: { className?: string }) {
  const linkClass = 'text-[var(--secondary-color)] hover:underline';
  return (
    <p className={['text-center text-xs text-gray-600', className].filter(Boolean).join(' ')}>
      By continuing, you agree to our{' '}
      <a
        href="/terms-of-service"
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        Terms of Service
      </a>{' '}
      and{' '}
      <a
        href="/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        className={linkClass}
      >
        Privacy Policy
      </a>.
    </p>
  );
}

export const PhoneAuthFlow = forwardRef<PhoneAuthFlowHandle, PhoneAuthFlowProps>(
  function PhoneAuthFlow({ onRequiresTotp, onSuccess, onStepChange, onSwitchToPassword }, ref) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { loginWithOtp, signupWithPhone } = useAuth();

    const [step, setStep] = useState<PhoneAuthStep>('phone');
    const [countryCode, setCountryCode] = useState('+91');
    const [phoneDisplay, setPhoneDisplay] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [verifiedCode, setVerifiedCode] = useState('');
    const [userExists, setUserExists] = useState<boolean | null>(null);

    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [creating, setCreating] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const [phoneError, setPhoneError] = useState('');
    const [otpError, setOtpError] = useState('');

    const digits = phoneDisplay.replace(/\D/g, '');
    const phoneNumber = `${countryCode}${digits}`;
    const phoneNumberDisplay = `${countryCode} ${digits}`;

    const phoneVerified = verifiedCode.length === 6 && verifiedCode === otpCode;

    const submittedCodeRef = useRef('');

    useEffect(() => {
      if (countdown > 0) {
        const t = setTimeout(() => setCountdown((n) => n - 1), 1000);
        return () => clearTimeout(t);
      }
    }, [countdown]);

    useEffect(() => {
      onStepChange?.(step, phoneNumberDisplay);
    }, [step, phoneNumberDisplay, onStepChange]);

    const resetToPhone = (message = '') => {
      setStep('phone');
      setOtpCode('');
      setOtpError('');
      setVerifiedCode('');
      setCountdown(0);
      setUserExists(null);
      submittedCodeRef.current = '';
      setPhoneError(message);
    };

    useImperativeHandle(
      ref,
      () => ({
        back: () => resetToPhone(),
      }),
      [],
    );

    const handleSendOtp = async () => {
      const pErr = validatePhoneForCountry(countryCode, phoneDisplay);
      if (pErr) {
        setPhoneError(pErr);
        return;
      }
      setPhoneError('');
      setSending(true);
      try {
        const result = await authApi.sendOTP(phoneNumber, undefined, undefined, 'auto');
        if (!result.data) {
          setPhoneError(result.error || 'Failed to send OTP');
          return;
        }
        if (typeof result.data.user_exists !== 'boolean') {
          setPhoneError('Could not start verification. Please try again.');
          return;
        }
        setUserExists(result.data.user_exists);
        setOtpCode('');
        submittedCodeRef.current = '';
        setStep('otp');
        setCountdown(60);
        toast({
          variant: 'success',
          title: 'OTP Sent!',
          description: 'Check your WhatsApp for the verification code.',
        });
      } catch (err: any) {
        setPhoneError(friendlyMessage(err, 'Failed to send OTP'));
      } finally {
        setSending(false);
      }
    };

    const handleResend = async () => {
      if (userExists === null) {
        resetToPhone('Please re-enter your phone number.');
        return;
      }
      setOtpError('');
      setSending(true);
      try {
        const result = await authApi.sendOTP(
          phoneNumber,
          undefined,
          undefined,
          userExists ? 'login' : 'signup',
        );
        if (!result.data) {
          if (result.errorCode === 'PHONE_EXISTS') {
            resetToPhone('This number now has an account. Please continue to log in.');
            return;
          }
          if (result.errorCode === 'PHONE_NOT_FOUND') {
            resetToPhone('This number no longer has an account. Please start again.');
            return;
          }
          setOtpError(result.error || 'Failed to send OTP');
          return;
        }
        setOtpCode('');
        setVerifiedCode('');
        submittedCodeRef.current = '';
        setCountdown(60);
        toast({
          variant: 'success',
          title: 'OTP Sent!',
          description: 'Check your WhatsApp for the verification code.',
        });
      } catch (err: any) {
        setOtpError(friendlyMessage(err, 'Failed to send OTP'));
      } finally {
        setSending(false);
      }
    };

    const referralCodeFromUrl = searchParams.get('ref') || searchParams.get('referral_code') || '';

    const completeSignup = async () => {
      setOtpError('');
      setCreating(true);
      try {
        const result = await signupWithPhone({
          phoneNumber,
          otpCode: otpCode.trim(),
          referralCode: referralCodeFromUrl || undefined,
        });
        if (result.success) {
          toast({
            title: 'Welcome to WeNext!',
            description: "Let's personalise your experience with a few quick questions.",
          });
          markNewSignupPending();
          navigate(ONBOARDING_V2_PREFLIGHT_PATH);
          return;
        }
        if (result.errorCode === 'PHONE_EXISTS') {
          resetToPhone('An account was just created with this number. Please log in with a new code.');
          return;
        }
        if (
          result.errorCode === 'PHONE_VERIFICATION_EXPIRED' ||
          result.errorCode === 'PHONE_NOT_VERIFIED'
        ) {
          resetToPhone(result.error || 'Phone verification expired. Please verify again.');
          return;
        }
        setOtpError(result.error || 'Could not finish creating your account.');
      } catch (err: any) {
        setOtpError(friendlyMessage(err, 'Could not finish creating your account.'));
      } finally {
        setCreating(false);
      }
    };

    const submitCode = async () => {
      if (otpCode.length !== 6) {
        setOtpError('Please enter the 6-digit code sent to your WhatsApp');
        return;
      }
      if (userExists === null) {
        resetToPhone('Please re-enter your phone number.');
        return;
      }
      setOtpError('');
      setVerifying(true);
      try {
        if (userExists) {
          const result = await loginWithOtp(phoneNumber, otpCode.trim());
          if (result.success) {
            if (result.requires_totp && result.userId) {
              onRequiresTotp(result.userId);
              return;
            }
            onSuccess();
            return;
          }
          if (result.errorCode === 'PHONE_AMBIGUOUS') {
            onSwitchToPassword?.(
              result.error || 'Multiple accounts use this phone number. Please sign in with email and password.',
            );
            return;
          }
          if (result.errorCode === 'OTP_LOCKED') {
            resetToPhone(result.error || 'Too many incorrect attempts. Please request a new code.');
            return;
          }
          setOtpError(result.error || 'Login failed');
          setOtpCode('');
          submittedCodeRef.current = '';
          return;
        }

        const result = await authApi.verifyOTP(phoneNumber, otpCode.trim());
        if (result.data?.verified) {
          toast({
            variant: 'success',
            title: 'Phone verified!',
            description: 'Your phone number has been verified successfully.',
          });
          setVerifiedCode(otpCode.trim());
          await completeSignup();
          return;
        }
        if (result.errorCode === 'OTP_LOCKED') {
          resetToPhone(result.error || 'Too many incorrect attempts. Please request a new code.');
          return;
        }
        setOtpError(result.error || 'Invalid OTP code');
        setOtpCode('');
        submittedCodeRef.current = '';
      } catch (err: any) {
        setOtpError(friendlyMessage(err, 'Verification failed'));
        setOtpCode('');
        submittedCodeRef.current = '';
      } finally {
        setVerifying(false);
      }
    };

    useEffect(() => {
      if (
        step === 'otp' &&
        otpCode.length === 6 &&
        !verifying &&
        !creating &&
        !phoneVerified &&
        submittedCodeRef.current !== otpCode
      ) {
        submittedCodeRef.current = otpCode;
        submitCode();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otpCode, step, phoneVerified]);

    const liveOverLengthError = phoneOverLengthError(countryCode, phoneDisplay);

    if (step === 'phone') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="auth-phone">Phone number</Label>
            <PhoneNumberInput
              id="auth-phone"
              className="h-12"
              countryCode={countryCode}
              phoneDisplay={phoneDisplay}
              invalid={!!phoneError || !!liveOverLengthError}
              onCountryChange={(code) => {
                setCountryCode(code);
                if (phoneError) setPhoneError('');
              }}
              onPhoneChange={(d) => {
                setPhoneDisplay(d);
                if (phoneError) setPhoneError('');
              }}
            />
            {(phoneError || liveOverLengthError) && (
              <p className="text-xs text-red-600">{phoneError || liveOverLengthError}</p>
            )}
          </div>
          <Button
            type="button"
            className="w-full !h-12 font-bold"
            onClick={handleSendOtp}
            disabled={sending}
          >
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
          <AuthLegalConsent className="!mt-[11px]" />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <OtpInput
          value={otpCode}
          onChange={(v) => {
            setOtpCode(v);
            if (otpError) setOtpError('');
          }}
          invalid={!!otpError}
          disabled={verifying || creating}
          autoFocus
        />

        {otpError && <p className="text-xs text-red-600">{otpError}</p>}
        {verifying && !creating && (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Verifying…
          </p>
        )}
        {creating && (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Setting up your account…
          </p>
        )}

        {phoneVerified && !creating && otpError && (
          <Button
            type="button"
            className="w-full !h-12 font-bold"
            onClick={completeSignup}
          >
            Try again
          </Button>
        )}

        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={handleResend}
            disabled={countdown > 0 || sending || creating}
            className="text-sm font-semibold text-[var(--secondary-color)] hover:underline disabled:no-underline disabled:opacity-60"
          >
            {countdown > 0 ? `Resend in ${countdown}s` : 'Resend code'}
          </button>
        </div>
      </div>
    );
  },
);
